import React, { useEffect, useState } from "react";
import { Flex, Button, Text } from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { ff } from "theme";
import type {
  CheckInMessageOption,
  CheckInResponseAction,
  InStoreCheckInStage,
} from "@foodfluence/shared";

/**
 * Quick-reply chips shown under a check-in message.
 *
 * The pre-visit "confirm" choice is the primary CTA and carries a countdown of the window the
 * creator has to respond before the check-in auto-cancels. The Shopify arrival check-in's
 * "Yes, got it!" is also primary, but with no countdown: nothing is canceled if they don't answer.
 * The remaining choices (reschedule / cancel, "Not yet" / "Something's wrong", or the uncancel
 * "I already visited" prompt) render as lighter secondary pills. When there is no primary option
 * (the uncancel choice message), every option is rendered as a secondary pill.
 *
 * Isolated into its own component so the countdown tick re-renders only the chips, not the whole
 * message thread.
 */

const HOUR_MS = 60 * 60 * 1000;

// Time from a check-in message's send until the collab auto-cancels, per stage.
// ponytail: mirrors CollabCheckInService (dashboard): FINAL_NOTICE_DELAY_MS=24h, then
// AUTO_CANCEL_DELAY_MS=24h-5m after the final notice. Drift risk if those change; upgrade path is
// to stamp an explicit `checkInDeadline` on the message and count to it. Only these two stages
// carry a confirm option, so only they ever reach the timer.
const MS_TO_AUTO_CANCEL: Partial<Record<InStoreCheckInStage, number>> = {
  preVisitInitial: 48 * HOUR_MS - 5 * 60 * 1000, // final notice (24h) + auto-cancel (24h-5m)
  preVisitFinalNotice: 24 * HOUR_MS - 5 * 60 * 1000, // auto-cancel (24h-5m)
};

// Coarse "1d 4h" / "23h" / "45m" — a 24-48h deadline never needs second precision.
function formatCountdown(msRemaining: number): string {
  const totalMin = Math.floor(msRemaining / 60000);
  if (totalMin <= 0) return "expiring";
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

// Concise button text. The original option.label is still what gets echoed into the thread as the
// creator's reply, so full phrasings like "I need to reschedule" read naturally there.
const DISPLAY_LABEL: Partial<Record<CheckInResponseAction, string>> = {
  confirm: "Confirm",
  reschedule: "Reschedule",
  cancel: "Cancel",
};

const labelFor = (option: CheckInMessageOption): string => DISPLAY_LABEL[option.action] ?? option.label;

// The answer we hope for renders as the dark primary pill.
const PRIMARY_ACTIONS: ReadonlySet<CheckInResponseAction> = new Set<CheckInResponseAction>([
  "confirm",
  "packageReceived",
]);

interface CheckInChipsProps {
  options: CheckInMessageOption[];
  /** ms-epoch send time of the check-in message these chips belong to (countdown anchor). */
  sentMs: number;
  /** Stage of the check-in message — picks the auto-cancel window for the countdown. */
  checkInStage?: InStoreCheckInStage;
  /** msgKey currently submitting (null when idle). */
  submittingKey: string | null;
  /** Which action is submitting, so only that chip shows a spinner. */
  submittingAction: CheckInResponseAction | null;
  msgKey: string;
  onChip: (action: CheckInResponseAction, label: string, msgKey: string) => void;
}

const CheckInChips: React.FC<CheckInChipsProps> = ({
  options,
  sentMs,
  checkInStage,
  submittingKey,
  submittingAction,
  msgKey,
  onChip,
}) => {
  const primaryOption = options.find((o) => PRIMARY_ACTIONS.has(o.action));
  const secondary = options.filter((o) => o !== primaryOption);
  // Only the pre-visit confirm has a deadline to count down to.
  const showCountdown = primaryOption?.action === "confirm";

  const [nowMs, setNowMs] = useState<number>(() => Date.now());
  useEffect(() => {
    if (!showCountdown) return; // only tick while a timed confirm chip is showing
    const id = window.setInterval(() => setNowMs(Date.now()), 60000);
    return () => window.clearInterval(id);
  }, [showCountdown]);

  const isBusy = submittingKey === msgKey;
  const windowMs = (checkInStage && MS_TO_AUTO_CANCEL[checkInStage]) ?? 24 * HOUR_MS;
  const countdown = formatCountdown(sentMs + windowMs - nowMs);

  return (
    <Flex direction="column" align="flex-start" gap="8px" mt="10px" maxW="100%">
      {primaryOption && (
        <Button
          onClick={() => onChip(primaryOption.action, primaryOption.label, msgKey)}
          isDisabled={isBusy && submittingAction !== primaryOption.action}
          isLoading={isBusy && submittingAction === primaryOption.action}
          loadingText="Confirming"
          h="44px"
          px="18px"
          minW="0"
          borderRadius="full"
          bg={ff.ink}
          color="white"
          fontFamily="display"
          fontSize="15px"
          fontWeight={600}
          _hover={{ bg: ff.inkSoft }}
          _active={{ bg: ff.inkSoft }}
          sx={{ WebkitTapHighlightColor: "transparent" }}
        >
          <Flex align="center" gap="8px">
            <Flex
              align="center"
              justify="center"
              w="18px"
              h="18px"
              borderRadius="full"
              border="1.5px solid rgba(255,255,255,0.45)"
              flexShrink={0}
            >
              <CheckIcon boxSize="9px" color="white" />
            </Flex>
            <Text as="span">{labelFor(primaryOption)}</Text>
            {showCountdown && (
              <>
                <Text as="span" color="rgba(255,255,255,0.4)" px="1px">
                  ·
                </Text>
                <Text
                  as="span"
                  color="rgba(255,255,255,0.85)"
                  fontWeight="500"
                  sx={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {countdown}
                </Text>
              </>
            )}
          </Flex>
        </Button>
      )}

      {secondary.length > 0 && (
        <Flex gap="8px" wrap="wrap" maxW="100%">
          {secondary.map((option) => (
            <Button
              key={option.action}
              onClick={() => onChip(option.action, option.label, msgKey)}
              isDisabled={isBusy && submittingAction !== option.action}
              isLoading={isBusy && submittingAction === option.action}
              h="44px"
              px="16px"
              minW="0"
              borderRadius="full"
              bg="white"
              color={ff.ink}
              border="1px solid"
              borderColor={ff.border}
              fontFamily="display"
              fontSize="14px"
              fontWeight={600}
              _hover={{ bg: ff.grey }}
              _active={{ bg: ff.line }}
              sx={{ WebkitTapHighlightColor: "transparent" }}
            >
              {labelFor(option)}
            </Button>
          ))}
        </Flex>
      )}
    </Flex>
  );
};

export default CheckInChips;
